/**
 * Palette checks: the part of choosing chart colors that can be computed, so
 * it is computed rather than eyeballed.
 *
 * Two callers:
 *
 *   - src/config/__tests__/themes.test.js holds every theme's data palette
 *     (the --data-* tokens in src/config/themes.js) to checkPalette in both
 *     modes, so a palette edit that makes two series hard to tell apart fails
 *     the build.
 *   - The chart renderer (A4, plan docs/plans/analytics.md d34 and d39) will
 *     run needsRelief over the colors every chart actually draws, in the
 *     order it draws them. Colors follow identity, so drawn neighbours can be
 *     any two palette slots, and a choice's own color is the author's data,
 *     which nothing checked in advance.
 *
 * Pure, dependency-free and small: a handful of 3x3 matrix products per color.
 * The measuring functions take "#rrggbb" or "#rgb" hex and throw on anything
 * else; needsRelief also reads the 22 choice color names (CHOICE_COLOR_HEX)
 * and turns relief on for anything it cannot read.
 *
 * The method (the data-visualization color formula):
 *
 *   ΔE      Euclidean distance in OKLab, times 100. OKLab is Björn Ottosson's
 *           perceptual space (2020, https://bottosson.github.io/posts/oklab/);
 *           the two matrices below are his, applied to linear sRGB.
 *   CVD     Color-vision deficiency, simulated with the matrices of Machado,
 *           Oliveira and Fernandes, "A Physiologically-based Model for
 *           Simulation of Color Vision Deficiency" (IEEE TVCG 15(6), 2009), at
 *           severity 1.0, applied to linear sRGB. The ΔE thresholds are
 *           calibrated to this model, so the model is part of the standard:
 *           swapping in another simulation would move borderline pairs.
 *   Gates   protanopia and deuteranopia (about 1 in 12 men between them).
 *           Tritanopia is reported beside them, never gated: it is rare, and
 *           the thresholds were not calibrated for it.
 *   Contrast WCAG 2.x relative luminance and contrast ratio, unrounded.
 */

// --- thresholds --------------------------------------------------------

export const THRESHOLDS = Object.freeze({
	// OKLCH lightness a categorical mark must sit inside, per mode: dark
	// enough to read on the surface, light enough to stay a color.
	band: Object.freeze({ light: Object.freeze([0.43, 0.77]), dark: Object.freeze([0.48, 0.67]) }),
	// OKLCH chroma below which a hue reads as grey and stops carrying identity.
	chromaFloor: 0.10,
	// Adjacent pairs under simulated protan and deutan vision. At or above the
	// target is a pass; between the floor and the target is legal only with a
	// second encoding (direct labels, gaps or a pattern); below the floor fails.
	cvdTarget: 8,
	cvdFloor: 6,
	// The same pairs under normal vision. A hard gate: a second encoding does
	// not excuse two colors a full-color reader cannot tell apart.
	normalFloor: 15,
	// WCAG ratio of a mark against the surface it is drawn on (1.4.11).
	contrastMin: 3,
});

// --- conversions -------------------------------------------------------

/** "#rrggbb" or "#rgb" → [r, g, b] in 0..1, or null. */
export function parseHex(value) {
	const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(value ?? '').trim());
	if (!match) return null;
	const hex = match[1].length === 3 ? match[1].split('').map((ch) => ch + ch).join('') : match[1];
	return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
}

const toLinear = (c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);

const linearRgb = (hex) => {
	const rgb = parseHex(hex);
	if (!rgb) throw new TypeError(`Not a hex color: ${hex}`);
	return rgb.map(toLinear);
};

const mul = (m, [r, g, b]) => [
	(m[0][0] * r) + (m[0][1] * g) + (m[0][2] * b),
	(m[1][0] * r) + (m[1][1] * g) + (m[1][2] * b),
	(m[2][0] * r) + (m[2][1] * g) + (m[2][2] * b),
];

// Ottosson 2020: linear sRGB → LMS, then the cube root, then LMS' → OKLab.
const OKLAB_M1 = [
	[0.4122214708, 0.5363325363, 0.0514459929],
	[0.2119034982, 0.6806995451, 0.1073969566],
	[0.0883024619, 0.2817188376, 0.6299787005],
];
const OKLAB_M2 = [
	[0.2104542553, 0.7936177850, -0.0040720468],
	[1.9779984951, -2.4285922050, 0.4505937099],
	[0.0259040371, 0.7827717662, -0.8086757660],
];

const oklabFromLinear = (lin) => mul(OKLAB_M2, mul(OKLAB_M1, lin).map(Math.cbrt));

/** OKLab [L, a, b] of a hex color. */
export function oklab(hex) {
	return oklabFromLinear(linearRgb(hex));
}

/** OKLCH [L, C, h°] of a hex color. */
export function oklch(hex) {
	const [L, a, b] = oklab(hex);
	return [L, Math.hypot(a, b), (((Math.atan2(b, a) * 180) / Math.PI) + 360) % 360];
}

// Machado, Oliveira & Fernandes 2009, severity 1.0, on linear sRGB.
const MACHADO = {
	protan: [
		[0.152286, 1.052583, -0.204868],
		[0.114503, 0.786281, 0.099216],
		[-0.003882, -0.048116, 1.051998],
	],
	deutan: [
		[0.367322, 0.860646, -0.227968],
		[0.280085, 0.672501, 0.047413],
		[-0.011820, 0.042940, 0.968881],
	],
	tritan: [
		[1.255528, -0.076749, -0.178779],
		[-0.078411, 0.930809, 0.147602],
		[0.004733, 0.691367, 0.303900],
	],
};

export const CVD_KINDS = Object.freeze(Object.keys(MACHADO));

const clamp01 = (c) => Math.max(0, Math.min(1, c));

/** The linear sRGB a reader with this deficiency sees, clamped to the gamut. */
export function simulateCvd(hex, kind) {
	const matrix = MACHADO[kind];
	if (!matrix) throw new RangeError(`Unknown deficiency: ${kind}`);
	return mul(matrix, linearRgb(hex)).map(clamp01);
}

/**
 * ΔE between two colors: OKLab distance ×100. With `kind` (protan, deutan,
 * tritan) both are first seen through that deficiency; without, as they are.
 */
export function deltaE(a, b, kind) {
	const see = (hex) => oklabFromLinear(kind ? simulateCvd(hex, kind) : linearRgb(hex));
	const [p, q] = [see(a), see(b)];
	return 100 * Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2]);
}

/** WCAG 2.x relative luminance. */
export function relativeLuminance(hex) {
	const [r, g, b] = linearRgb(hex);
	return (0.2126 * r) + (0.7152 * g) + (0.0722 * b);
}

/** WCAG 2.x contrast ratio, unrounded (a rounded 2.996 must not pass 3:1). */
export function contrastRatio(a, b) {
	const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
	return (hi + 0.05) / (lo + 0.05);
}

// --- checks ------------------------------------------------------------

// Which pairs of marks can touch: 'adjacent' (bars, stacks, lines, areas:
// neighbours in legend order), 'ring' (a donut: neighbours, and the last
// slice against the first), or 'all' (a heat map's legend, or anything
// unordered, where any two may sit side by side).
const pairList = (n, pairs) => {
	const out = [];
	for (let i = 0; i < n; i += 1) {
		if (pairs === 'all') for (let j = i + 1; j < n; j += 1) out.push([i, j]);
		else if (i + 1 < n) out.push([i, i + 1]);
	}
	if (pairs === 'ring' && n > 2) out.push([n - 1, 0]);
	return out;
};

const worstPair = (colors, list, kinds) => {
	let worst = null;
	kinds.forEach((kind) => {
		list.forEach(([i, j]) => {
			const value = deltaE(colors[i], colors[j], kind);
			if (!worst || value < worst.deltaE) worst = { deltaE: value, pair: [i, j], kind: kind || 'normal' };
		});
	});
	return worst;
};

/**
 * The categorical checks for one palette in one mode, against the surface it
 * is drawn on. `pairs` is 'adjacent' (bars, stacks, lines: only neighbours
 * touch, so only neighbours are compared), 'ring' (a donut, whose last slice
 * touches its first) or 'all' (any two marks may sit side by side).
 *
 * Each check answers { state: 'pass' | 'warn' | 'fail', … } with its numbers;
 * `ok` is false when any check fails. A warn is legal only with the relief it
 * names: a second encoding for CVD, direct labels or the table for contrast.
 */
export function checkPalette({ colors, surface, mode = 'light', pairs = 'adjacent' } = {}) {
	const list = Array.isArray(colors) ? colors : [];
	const [lo, hi] = THRESHOLDS.band[mode === 'dark' ? 'dark' : 'light'];
	const lch = list.map(oklch);
	const pairsToCheck = pairList(list.length, pairs);

	const outside = lch.map(([L], index) => ({ index, L })).filter(({ L }) => L < lo || L > hi);
	const band = { state: outside.length ? 'fail' : 'pass', range: [lo, hi], outside, lightness: lch.map(([L]) => L) };

	const grey = lch.map(([, C], index) => ({ index, C })).filter(({ C }) => C < THRESHOLDS.chromaFloor);
	const chroma = { state: grey.length ? 'fail' : 'pass', floor: THRESHOLDS.chromaFloor, below: grey, chroma: lch.map(([, C]) => C) };

	const cvdWorst = worstPair(list, pairsToCheck, ['protan', 'deutan']);
	const tritanWorst = worstPair(list, pairsToCheck, ['tritan']);
	const cvdValue = cvdWorst ? cvdWorst.deltaE : Infinity;
	const cvd = {
		state: cvdValue >= THRESHOLDS.cvdTarget ? 'pass' : cvdValue >= THRESHOLDS.cvdFloor ? 'warn' : 'fail',
		target: THRESHOLDS.cvdTarget,
		floor: THRESHOLDS.cvdFloor,
		worst: cvdWorst,
		tritan: tritanWorst,
	};

	const normalWorst = worstPair(list, pairsToCheck, [undefined]);
	const normal = {
		state: (normalWorst ? normalWorst.deltaE : Infinity) >= THRESHOLDS.normalFloor ? 'pass' : 'fail',
		floor: THRESHOLDS.normalFloor,
		worst: normalWorst,
	};

	const ratios = parseHex(surface) ? list.map((hex) => contrastRatio(hex, surface)) : [];
	const faint = ratios.map((ratio, index) => ({ index, ratio })).filter(({ ratio }) => ratio < THRESHOLDS.contrastMin);
	const contrast = {
		state: !ratios.length && list.length ? 'fail' : faint.length ? 'warn' : 'pass',
		min: THRESHOLDS.contrastMin,
		ratios,
		below: faint,
		lowest: ratios.length ? Math.min(...ratios) : null,
	};

	const checks = { band, chroma, cvd, normal, contrast };
	return { ok: Object.values(checks).every((check) => check.state !== 'fail'), checks };
}

// --- a chart's own colors ----------------------------------------------

/**
 * The 22 colors a choice may carry (src/utils/dictionary.js `elementColors`,
 * stored as CSS color names), with their CSS Color Module values. They are
 * the author's data, not theme colors: this table exists only so a chart can
 * measure what it drew, and nothing paints from it. A test holds it to the
 * dictionary's list, so a color added there must be added here.
 */
export const CHOICE_COLOR_HEX = Object.freeze({
	olive: '#808000',
	forestgreen: '#228b22',
	limegreen: '#32cd32',
	palegreen: '#98fb98',
	gold: '#ffd700',
	darkorange: '#ff8c00',
	tomato: '#ff6347',
	crimson: '#dc143c',
	deeppink: '#ff1493',
	violet: '#ee82ee',
	darkmagenta: '#8b008b',
	paleturquoise: '#afeeee',
	dodgerblue: '#1e90ff',
	darkcyan: '#008b8b',
	aqua: '#00ffff',
	wheat: '#f5deb3',
	tan: '#d2b48c',
	sandybrown: '#f4a460',
	chocolate: '#d2691e',
	saddlebrown: '#8b4513',
	lightgray: '#d3d3d3',
	darkgray: '#a9a9a9',
});

/** A hex color, or one of the choice colors by name (any case); else null. */
export function resolveColor(value) {
	if (parseHex(value)) return String(value).trim();
	const key = String(value ?? '').trim().toLowerCase();
	return Object.prototype.hasOwnProperty.call(CHOICE_COLOR_HEX, key) ? CHOICE_COLOR_HEX[key] : null;
}

/** Which pairs of a form's marks can touch (see pairList). */
export const FORM_PAIRS = Object.freeze({
	bar: 'adjacent',
	stack: 'adjacent',
	line: 'adjacent',
	area: 'adjacent',
	donut: 'ring',
	heatmap: 'all',
});

/**
 * The chart's own test of the colors it drew (plan decision d39, inside the
 * owner's d34: color alone must never carry identity). Every chart runs it,
 * palette colors included: colors follow identity, so the colors a chart
 * draws side by side can be any two slots, not the neighbours the palette
 * test checked, and a choice's own color was never checked at all.
 *
 *   colors   in drawn order: the legend's order for bars, stacks, lines and
 *            areas; the slices' order for a donut. Hex, or a choice color's
 *            CSS name.
 *   surface  the hex the chart is drawn on, in the current mode.
 *   form     'bar' | 'stack' | 'line' | 'area' (neighbours), 'donut'
 *            (neighbours and the last against the first), 'heatmap' or
 *            anything else (every pair).
 *
 * Relief (direct labels and patterns) turns on when any checked pair is under
 * ΔE 8 for a protan or deutan reader or under ΔE 15 for everyone, when any
 * color is under 3:1 on the surface, and whenever it cannot tell: a color it
 * cannot resolve, or no surface to measure against. It fails closed.
 *
 * Returns { relief, pairs, cvd, closest, faint, unresolved }: `cvd` and
 * `closest` are the worst checked pair under protan/deutan and under normal
 * vision, `faint` the indexes under 3:1, `unresolved` the indexes it could
 * not read.
 */
export function needsRelief({ colors, surface, form } = {}) {
	const list = Array.isArray(colors) ? colors : [];
	const pairs = FORM_PAIRS[form] || 'all';
	const resolved = list.map(resolveColor);
	const unresolved = resolved.map((hex, index) => (hex ? -1 : index)).filter((index) => index >= 0);
	const drawn = resolved.map((hex, index) => ({ hex, index })).filter(({ hex }) => hex);
	const hexes = drawn.map(({ hex }) => hex);
	const checked = pairList(hexes.length, pairs);
	const toIndexes = (worst) => worst && { ...worst, pair: worst.pair.map((i) => drawn[i].index) };
	const cvd = toIndexes(worstPair(hexes, checked, ['protan', 'deutan']));
	const closest = toIndexes(worstPair(hexes, checked, [undefined]));
	const surfaceKnown = !!parseHex(surface);
	const faint = surfaceKnown
		? drawn.filter(({ hex }) => contrastRatio(hex, surface) < THRESHOLDS.contrastMin).map(({ index }) => index)
		: [];
	const relief = unresolved.length > 0
		|| !surfaceKnown
		|| faint.length > 0
		|| (!!cvd && cvd.deltaE < THRESHOLDS.cvdTarget)
		|| (!!closest && closest.deltaE < THRESHOLDS.normalFloor);
	return { relief: list.length > 0 && relief, pairs, cvd, closest, faint, unresolved };
}
